<script setup lang="ts">
import { ref, watch } from 'vue'
import DateInput from '../timinplanner/DateInput.vue'
import ConfirmModal from '../timinplanner/ConfirmModal.vue'
import { sendRequest, projectPeriods } from '../timinplanner/planner.js'
import { toGerman, fromGerman, addDays, toIsoLocal } from '@/utils/dateUtils'

// Zeitabschnitte eines Projekts bearbeiten (TIM-38). Bewusst mit eigenem
// Speicherweg statt ueber das normale Formular: nur so greifen dieselben Regeln
// wie im Planer - Pausen unter einer Woche werden zusammengefasst, und vor dem
// Kuerzen oder Loeschen von Buchungen wird gefragt.
const props = defineProps<{ project: any }>()
const emit = defineEmits<{ saved: [periods: any[]] }>()

const rows = ref<{ start: string, end: string }[]>([])
const confirmModal = ref<any>(null)
const modalMessage = ref('')
const isSaving = ref(false)
const hinweis = ref('')

watch(() => props.project, (p) => {
  const periods = projectPeriods(p ?? {})
  rows.value = periods.map((pd: any) => ({ start: toGerman(pd.start), end: toGerman(pd.end) }))
}, { immediate: true, deep: false })

function addRow() {
  const last = rows.value[rows.value.length - 1]
  const from = last ? addDays(fromGerman(last.end), 8) : toIsoLocal(new Date())
  rows.value.push({ start: toGerman(from), end: toGerman(addDays(from, 6)) })
}

function speichern() {
  hinweis.value = ''
  const periods = rows.value
    .map(r => ({ start: fromGerman(r.start), end: fromGerman(r.end) }))
    .filter(r => r.start && r.end)

  if (periods.length === 0) { hinweis.value = 'Es muss mindestens ein Zeitabschnitt bleiben.'; return }
  if (periods.some(r => r.end < r.start)) { hinweis.value = 'Ein Abschnitt endet vor seinem Anfang.'; return }

  isSaving.value = true
  sendRequest('/update_project_periods', { id: props.project.id, periods, preview: true }, async (vorschau: any) => {
    isSaving.value = false
    const c = vorschau['changed_bookings'] || {}
    const teile = []
    if (c.trimmed) teile.push(`${c.trimmed} gekürzt`)
    if (c.deleted) teile.push(`${c.deleted} entfernt`)
    if (c.split)   teile.push(`${c.split} in zwei einzelne geteilt`)

    if (teile.length > 0) {
      modalMessage.value = `Änderungen bei Buchungen: ${teile.join(', ')}`
      const ok = await confirmModal.value.open()
      if (!ok) return
    }
    wirklichSpeichern(periods)
  }, (fehler: any) => {
    isSaving.value = false
    hinweis.value = fehler['error'] ?? 'Speichern fehlgeschlagen.'
  })
}

function wirklichSpeichern(periods: any[]) {
  isSaving.value = true
  sendRequest('/update_project_periods', { id: props.project.id, periods }, (antwort: any) => {
    isSaving.value = false
    rows.value = (antwort['periods'] ?? []).map((pd: any) => ({ start: toGerman(pd.start), end: toGerman(pd.end) }))
    hinweis.value = 'Gespeichert.'
    emit('saved', antwort['periods'] ?? [])
  }, (fehler: any) => {
    isSaving.value = false
    hinweis.value = fehler['error'] ?? 'Speichern fehlgeschlagen.'
  })
}
</script>

<template>
  <div class="flex flex-col gap-2">
    <div v-for="(row, i) in rows" :key="`period-${i}`" class="flex flex-wrap items-center gap-2">
      <DateInput label="von" :modelValue="row.start" @datechange="(v) => row.start = v" />
      <DateInput label="bis" :modelValue="row.end" @datechange="(v) => row.end = v" />
      <button v-if="rows.length > 1" type="button" title="Abschnitt entfernen"
              class="px-2 py-1 rounded border border-gray-300 hover:bg-gray-100 text-gray-600 leading-none"
              @click="rows.splice(i, 1)">−</button>
    </div>

    <div class="flex items-center gap-4">
      <button type="button" class="text-sm text-gray-600 hover:underline" @click="addRow()">+ Abschnitt</button>
      <button type="button" :disabled="isSaving"
              class="px-3 py-1 rounded bg-timin hover:brightness-90 text-black text-sm disabled:opacity-50"
              @click="speichern()">Zeiträume speichern</button>
      <span v-if="hinweis" class="text-xs text-gray-600">{{ hinweis }}</span>
    </div>

    <p class="text-xs text-gray-500">Pausen unter einer Woche werden zusammengefasst.</p>

    <ConfirmModal ref="confirmModal" :message="modalMessage" :items="[]" :cancelable="true" />
  </div>
</template>
